/**
 * A HEIGHT THAT DOES NOT MOVE WHEN AN IN-APP BROWSER'S TOOLBAR DOES.
 *
 * The product page caps its photo stage by the viewport's height, so that on a phone the name and
 * the price stay above the fold (UX-SHOP-27, `Pages/ProductDetail/styles.module.css`). It was
 * written against `100svh`, which in Safari is the height with the bars SHOWN and does not move.
 *
 * Instagram's and Facebook's in-app browsers on iOS are a WKWebView the app resizes itself: when the
 * shopper first scrolls, the app's toolbar collapses and the web view grows. Every viewport unit —
 * `vh`, `svh`, `lvh`, `dvh` — and `innerHeight` grow with it. So a shopper arriving from an ad saw
 * the photographs drawn small, and saw them jump to their full size on the first scroll.
 *
 * In those browsers the page is given `--stable-vh`: the height the web view reaches once the
 * toolbar is out of the way, known before it happens — the screen's height in the current
 * orientation less the status bar and the bar the app keeps — and never lower than any height
 * actually seen. It changes when the orientation does, not when the toolbar does. Everywhere else
 * nothing is set and the stylesheet keeps `svh`.
 */

/** Instagram, Facebook and Messenger (FBAN/FBAV/FB_IAB) — the in-app browsers the ads open in. */
const IN_APP_UA = /\b(Instagram|FBAN|FBAV|FB_IAB|FBIOS|Messenger)\b/i;

/** The status bar and the app's own collapsed bar, which the web view never gets. */
export const IN_APP_CHROME_PX = 100;

export function isInAppBrowser(userAgent) {
  return typeof userAgent === 'string' && IN_APP_UA.test(userAgent);
}

/**
 * The height the page will have with the in-app toolbar collapsed: the screen's side that is
 * vertical right now, less `IN_APP_CHROME_PX`, and at least `seen`.
 */
export function stableViewportHeight({ screenWidth, screenHeight, viewportWidth, viewportHeight, seen = 0 }) {
  const long = Math.max(screenWidth || 0, screenHeight || 0);
  const short = Math.min(screenWidth || 0, screenHeight || 0);
  const landscape = (viewportWidth || 0) > (viewportHeight || 0);
  const screenSide = landscape ? short : long;
  const fromScreen = screenSide > 0 ? screenSide - IN_APP_CHROME_PX : 0;
  return Math.round(Math.max(fromScreen, viewportHeight || 0, seen || 0));
}

/**
 * THE HEIGHT THE SHOPPER ARRIVED WITH (2026-11-03). The product page fits the name and the price above the add-to-cart
 * bar in the FIRST view, which in an in-app browser is the one with the app's toolbar shown — the smallest height
 * seen in this orientation, not `--stable-vh` (the toolbar gone). A height under 60% of the screen's side is a keyboard,
 * not an arrival, and is ignored.
 */
export function arrivalViewportHeight({ screenSide, viewportHeight, seen = 0 }) {
  const h = viewportHeight || 0;
  const usable = h > 0 && !(screenSide > 0 && h < screenSide * 0.6);
  if (!usable) return seen || 0;
  return Math.round(seen > 0 ? Math.min(seen, h) : h);
}

/**
 * Publish `--stable-vh`, `--arrival-vh` (and `data-in-app-browser`) on <html> when running in an in-app browser.
 * Returns an uninstall function; a no-op anywhere else.
 */
export function installStableViewportHeight(win = typeof window !== 'undefined' ? window : undefined) {
  if (!win || !win.document || !isInAppBrowser(win.navigator && win.navigator.userAgent)) return () => {};
  const root = win.document.documentElement;
  root.setAttribute('data-in-app-browser', '');
  let seen = 0;
  let arrived = 0;
  let lastLandscape = null;
  const update = () => {
    const vv = win.visualViewport;
    const viewportWidth = win.innerWidth || (vv && vv.width) || 0;
    const viewportHeight = Math.max(win.innerHeight || 0, (vv && vv.height) || 0);
    const landscape = viewportWidth > viewportHeight;
    // A turn of the phone starts over; a toolbar only ever adds to what was seen.
    if (landscape !== lastLandscape) { seen = 0; arrived = 0; lastLandscape = landscape; }
    const height = stableViewportHeight({
      screenWidth: win.screen && win.screen.width,
      screenHeight: win.screen && win.screen.height,
      viewportWidth,
      viewportHeight,
      seen,
    });
    seen = height;
    root.style.setProperty('--stable-vh', `${height}px`);
    const long = Math.max((win.screen && win.screen.width) || 0, (win.screen && win.screen.height) || 0);
    const short = Math.min((win.screen && win.screen.width) || 0, (win.screen && win.screen.height) || 0);
    // innerHeight only: a keyboard shrinks the visual viewport, not the page's first view.
    arrived = arrivalViewportHeight({ screenSide: landscape ? short : long, viewportHeight: win.innerHeight || 0, seen: arrived });
    if (arrived > 0) root.style.setProperty('--arrival-vh', `${arrived}px`);
  };
  update();
  win.addEventListener('resize', update);
  win.addEventListener('orientationchange', update);
  const vv = win.visualViewport;
  if (vv && vv.addEventListener) vv.addEventListener('resize', update);
  return () => {
    win.removeEventListener('resize', update);
    win.removeEventListener('orientationchange', update);
    if (vv && vv.removeEventListener) vv.removeEventListener('resize', update);
  };
}

/* THERE IS NO `--vv-bottom-gap` ANY MORE (2026-10-06).
 *
 * This file used to publish `innerHeight - (visualViewport.height + offsetTop)` on <html> from a
 * `visualViewport` resize/scroll listener, and the product page's add-to-cart bar sat at that
 * `bottom`. On iPhone Safari the two heights move on different frames while the toolbar collapses, so
 * the bar jumped up and dropped back on every scroll — the owner's "isn't sticky well". A fixed bar
 * belongs at `bottom: 0` with nothing in JavaScript repositioning it; Safari moves it with the
 * toolbar itself. Do not bring it back for the strip of page that shows under a fixed bar while the
 * page is pinch-zoomed or rubber-banded: every fixed bar on every site shows that.
 */
